import { useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import type { Feature, FeatureCollection, Polygon, MultiPolygon } from 'geojson';
import { useAuth } from '@vehix/shared/admin/portal-bridge';
import type { Zone } from '@vehix/portal/services/api/tenants';
import { bboxForGeometry, fetchZipCodeBoundaries, type BBox } from '../services/zipCodeBoundaries';

/**
 * Per-zone ZIP-boundary fetcher used by Presentation mode to render
 * subdivision lines inside each zone polygon. Each zone's `zip_codes` array
 * is fanned out to the existing Census TIGER edge function and reassembled
 * into a `Map<zoneId, FeatureCollection>` ready to feed `L.geoJSON()`.
 *
 * Cache strategy mirrors {@link useCurrentRecallCounts}:
 *  - Zone-id input is canonicalized (sorted + de-duped) so any permutation
 *    of the same zone set hits one cache entry.
 *  - 30-minute staleTime — Census boundaries are essentially static.
 *  - keepPreviousData so toggling a single zone in/out of the selection
 *    doesn't flash the map back to "no subdivisions" while the new fetch
 *    is in flight.
 *
 * The result is empty until ZIP boundaries arrive. The renderer must
 * handle the loading state (zone polygon + halo render fine without
 * subdivisions; subdivisions appear when ready).
 */
export interface ZoneZipBoundaryMap {
  byZone: Map<string, FeatureCollection<Polygon | MultiPolygon>>;
}

const QUERY_KEY_PREFIX = ['zoneZipBoundaries'] as const;

interface ZoneBoundaryRequest {
  id: string;
  zip_codes: string[];
  // Box of the zone's own outline — lets get-zips answer in <1 s instead of ~14 s
  bbox: BBox | undefined;
}

async function fetchAllBoundariesForZones(
  zones: ZoneBoundaryRequest[]
): Promise<ZoneZipBoundaryMap> {
  const byZone = new Map<string, FeatureCollection<Polygon | MultiPolygon>>();

  // Run per-zone fetches in parallel. The edge function is rate-limited but
  // a handful of concurrent calls is well within its budget; this keeps
  // total wall-clock latency at ~max(per-zone latency) rather than
  // sum(per-zone latency).
  await Promise.all(
    zones.map(async zone => {
      const zips = zone.zip_codes;
      if (zips.length === 0) {
        byZone.set(zone.id, { type: 'FeatureCollection', features: [] });
        return;
      }
      try {
        const features = await fetchZipCodeBoundaries(zips, { bbox: zone.bbox });
        byZone.set(zone.id, {
          type: 'FeatureCollection',
          features: features as Feature<Polygon | MultiPolygon>[]
        });
      } catch {
        // Defensive: a single zone's boundary fetch failing shouldn't block
        // the whole batch. The map will simply omit subdivisions for that
        // zone — the polygon + halo still render normally.
        byZone.set(zone.id, { type: 'FeatureCollection', features: [] });
      }
    })
  );

  return { byZone };
}

export function useZoneZipBoundaries(
  zones: Pick<Zone, 'id' | 'zip_codes' | 'metadata'>[],
  options?: { enabled?: boolean }
) {
  const { session, loading: authLoading } = useAuth();

  // Canonicalize the zone-id list so any permutation shares one cache entry.
  // We also need a stable reference to the zone-objects for the queryFn —
  // the cache key only depends on ids+zip_code counts, but the actual fetch
  // needs the zip_codes themselves. The bbox is computed here so the large
  // geometry itself never travels into the queryFn closure.
  const canonicalZones = useMemo((): ZoneBoundaryRequest[] => {
    return [...zones]
      .map(z => ({
        id: z.id,
        zip_codes: z.zip_codes ?? [],
        bbox: bboxForGeometry(z.metadata?.['geometry'])
      }))
      .sort((a, b) => a.id.localeCompare(b.id));
  }, [zones]);

  const cacheKey = useMemo(
    () => canonicalZones.map(z => `${z.id}:${z.zip_codes.length}`).join('|'),
    [canonicalZones]
  );

  return useQuery<ZoneZipBoundaryMap>({
    queryKey: [...QUERY_KEY_PREFIX, cacheKey],
    queryFn: () => fetchAllBoundariesForZones(canonicalZones),
    enabled:
      (options?.enabled ?? true) &&
      canonicalZones.length > 0 &&
      !!session &&
      !authLoading,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData
  });
}
